"use client";

import Image from "next/image";
import Link from "next/link";
import { Phone, ArrowRight, Check, AlertTriangle, CloudRain, Clock, Shield, BookOpen, ExternalLink } from "lucide-react";
import { BRAND } from "@/lib/brand";

const F = { display: "'Plus Jakarta Sans', sans-serif", body: '"Barlow", sans-serif', mono: '"DM Mono", monospace' };
const C = { gold: "#D4A84B", white: "#ffffff", w82: "rgba(255,255,255,0.82)", w55: "rgba(255,255,255,0.70)", w30: "rgba(255,255,255,0.30)", navy: "#080F1A", navyMid: "#0d1929", red: "rgba(220,38,38,0.9)" };
const CARD: React.CSSProperties = { background: "rgba(255,255,255,0.10)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", border: "1px solid rgba(255,255,255,0.15)", borderTop: `2px solid ${C.gold}`, borderRadius: 14, padding: "2rem", display: "flex", flexDirection: "column" };
const PANEL: React.CSSProperties = { background: "rgba(255,255,255,0.12)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 16, padding: "2.5rem" };
const LABEL: React.CSSProperties = { color: C.gold, fontFamily: F.mono, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", display: "block", marginBottom: "0.6rem" };
const H2: React.CSSProperties = { color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "clamp(1.8rem, 3.5vw, 3rem)", marginBottom: "0.75rem", lineHeight: 1.1 };
const H3: React.CSSProperties = { color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "1.25rem", marginBottom: "0.75rem" };
const P: React.CSSProperties = { color: C.w82, fontSize: "0.95rem", lineHeight: 1.75, fontFamily: F.body };
const OVERLAY = (o: number): React.CSSProperties => ({ position: "absolute", inset: 0, background: `rgba(5,13,24,${o})`, zIndex: 1 });
const CONTENT: React.CSSProperties = { position: "relative", zIndex: 2, padding: "5rem 1.5rem", maxWidth: 1200, margin: "0 auto", width: "100%" };
const SEC: React.CSSProperties = { position: "relative", overflow: "hidden", backgroundColor: C.navy };
const GOLDLINE: React.CSSProperties = { width: 48, height: 2, background: C.gold, borderRadius: 2, margin: "16px 0" };
const BTN_RED: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 10, background: "linear-gradient(135deg, #DC2626 0%, #dc2626 100%)", color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "1.1rem", borderRadius: 12, padding: "16px 36px", textDecoration: "none", border: "none", letterSpacing: "1px", boxShadow: "0 8px 32px rgba(220,38,38,0.55), 0 2px 8px rgba(220,38,38,0.4)" };
const BTN_GOLD: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 8, background: "transparent", color: C.gold, fontFamily: F.body, fontWeight: 600, fontSize: "1rem", border: `1.5px solid ${C.gold}`, borderRadius: 10, padding: "13px 26px", textDecoration: "none" };

const fastFacts = [
  { label: "Monsoon Season", value: "June 15 – September 30", note: "Official NWS definition" },
  { label: "Avg. Phoenix Rainfall", value: "2.43 inches", note: "Historical monsoon average" },
  { label: "Peak Storm Window", value: "July – August", note: "Highest intensity events" },
  { label: "Critical Response Window", value: "24 – 48 Hours", note: "Per IICRC S500" },
];

const commonLosses = [
  "Flash flooding",
  "Roof intrusion",
  "HVAC overflow",
  "Window seal failure",
];

const checklist = [
  "Roof membrane and flashing inspected and sealed",
  "HVAC condensate drain lines cleared and tested",
  "Window and door seals inspected — replace any cracked caulking",
  "Roof drains and scuppers cleared of debris",
  "Parking lot drains and catch basins cleared",
  "Sump pumps tested if applicable",
  "Exterior penetrations (pipes, conduit, vents) sealed",
  "Landscaping graded away from foundation",
  "Irrigation systems checked for leaks near the building",
  "Emergency shutoff valve locations documented and accessible",
  "Insurance policy reviewed — know your water damage coverage limits",
  "Verify your restoration contractor is licensed before an emergency — roc.az.gov",
  "Have emergency contact numbers posted at the property",
  "Document current property condition with photos — timestamped",
  "Know your TPA or insurance carrier's preferred vendor requirements",
];

export default function MonsoonWatchPage() {
  return (
    <>
      {/* ═══ HERO ═══ */}
      <section style={{ ...SEC, minHeight: "80vh", display: "flex", alignItems: "center" }}>
        <Image src="/images/arizona-landmarks/saguaro-cactus-desert-arizona.jpg" alt="Arizona monsoon landscape" fill style={{ objectFit: "cover" }} priority />
        <div style={OVERLAY(0.62)} />
        <div style={{ ...CONTENT, maxWidth: 900, textAlign: "center" }}>
          <span style={LABEL}>Seasonal Resource · Updated 2026</span>
          <h1 style={{ color: C.white, fontFamily: F.display, fontWeight: 800, fontSize: "clamp(2rem, 4.5vw, 3.8rem)", lineHeight: 1.05, marginBottom: 20 }}>
            Arizona Monsoon Watch 2026.<br />What Property Managers Need to Know Now.
          </h1>
          <div style={{ ...GOLDLINE, margin: "20px auto" }} />
          <p style={{ ...P, fontSize: "1.1rem", maxWidth: 720, margin: "0 auto" }}>
            The National Weather Service is forecasting a warmer and wetter monsoon season for 2026.
            If your property isn&rsquo;t prepared, the storm will find the weakness. Here&rsquo;s what to do before it hits.
          </p>
        </div>
      </section>

      {/* ═══ 2026 FORECAST ═══ */}
      <section style={{ ...SEC, backgroundColor: C.navyMid }}>
        <div style={{ ...CONTENT, maxWidth: 900 }}>
          <div style={{ marginBottom: "2rem" }}>
            <span style={LABEL}>Forecast</span>
            <h2 style={H2}>What NWS Is Saying About 2026</h2>
            <div style={GOLDLINE} />
          </div>
          <div style={{ ...PANEL, padding: "2.5rem" }}>
            <CloudRain size={32} style={{ color: C.gold, marginBottom: 20 }} />
            <p style={{ ...P, marginBottom: 16 }}>
              The National Weather Service is forecasting <strong style={{ color: C.white }}>above-normal
              temperatures and above-normal precipitation</strong> for Arizona&rsquo;s 2026 monsoon season.
            </p>
            <p style={{ ...P, marginBottom: 16 }}>
              Phoenix broke <strong style={{ color: C.white }}>7 consecutive temperature records in March 2026</strong>,
              including the earliest 100°F day ever recorded (March 18). HVAC systems strained by early heat
              are more vulnerable to water intrusion events when monsoon moisture arrives.
            </p>
            <p style={{ ...P, marginBottom: 16 }}>
              FEMA flood map updates for Maricopa County are pending — verify your property&rsquo;s current
              flood zone designation at{" "}
              <a href="https://msc.fema.gov" target="_blank" rel="noopener noreferrer" style={{ color: C.gold, fontWeight: 600, textDecoration: "underline" }}>
                msc.fema.gov
              </a>.
            </p>
            <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 10, padding: "1rem 1.25rem", marginTop: 20, display: "flex", gap: 12, alignItems: "flex-start" }}>
              <BookOpen size={16} style={{ color: C.w55, flexShrink: 0, marginTop: 3 }} />
              <p style={{ ...P, fontSize: "0.8rem", color: C.w55 }}>
                <strong style={{ color: C.white }}>Disclaimer:</strong> Forecast data is sourced from publicly available
                National Weather Service and FEMA publications. DRR does not guarantee forecast accuracy. Always verify
                current conditions at{" "}
                <a href="https://weather.gov" target="_blank" rel="noopener noreferrer" style={{ color: C.gold, fontWeight: 600 }}>weather.gov</a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FAST FACTS ═══ */}
      <section style={SEC}>
        <div style={CONTENT}>
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <span style={LABEL}>Fast Facts</span>
            <h2 style={H2}>Arizona Monsoon at a Glance</h2>
            <div style={{ ...GOLDLINE, margin: "16px auto" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1.25rem", marginBottom: "2rem" }}>
            {fastFacts.map((f) => (
              <div key={f.label} style={{ ...CARD, textAlign: "center", padding: "2rem 1.5rem" }}>
                <span style={{ ...LABEL, marginBottom: 12 }}>{f.label}</span>
                <span style={{ color: C.gold, fontFamily: F.display, fontWeight: 800, fontSize: "1.8rem", lineHeight: 1.1, display: "block", marginBottom: 8 }}>{f.value}</span>
                <span style={{ ...P, fontSize: "0.78rem", color: C.w55 }}>{f.note}</span>
              </div>
            ))}
          </div>
          <div style={{ ...PANEL, padding: "2rem 2.5rem", maxWidth: 800, margin: "0 auto" }}>
            <span style={{ ...LABEL, marginBottom: 12 }}>Most Common Loss Types</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {commonLosses.map((l) => (
                <span key={l} style={{ background: "rgba(212,168,75,0.12)", border: "1px solid rgba(212,168,75,0.25)", borderRadius: 20, padding: "6px 16px", fontFamily: F.body, fontSize: "0.88rem", fontWeight: 600, color: C.gold }}>
                  {l}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 15-POINT CHECKLIST ═══ */}
      <section style={{ ...SEC, backgroundColor: C.navyMid }}>
        <div style={{ ...CONTENT, maxWidth: 900 }}>
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <span style={LABEL}>Pre-Monsoon Property Checklist</span>
            <h2 style={H2}>15 Things to Check Before Monsoon Season</h2>
            <div style={{ ...GOLDLINE, margin: "16px auto" }} />
          </div>
          <div style={{ ...PANEL, padding: "2rem 2.5rem" }}>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {checklist.map((item, i) => (
                <li key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "0.9rem 0", borderBottom: i < checklist.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                  <span style={{ color: C.gold, fontFamily: F.mono, fontSize: "10px", fontWeight: 700, minWidth: 24, paddingTop: 4 }}>{String(i + 1).padStart(2, "0")}</span>
                  <div style={{ width: 22, height: 22, borderRadius: 6, border: `1.5px solid ${C.gold}`, flexShrink: 0, marginTop: 1, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(212,168,75,0.08)" }}>
                    <Check size={13} style={{ color: C.gold }} />
                  </div>
                  <span style={{ ...P, flex: 1, fontSize: "0.92rem" }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ═══ 24-HOUR CLOCK ═══ */}
      <section style={{ ...SEC, minHeight: "50vh" }}>
        <Image src="/images/arizona-landmarks/sedona-cathedral-rock-arizona.jpg" alt="Arizona storm landscape" fill style={{ objectFit: "cover" }} />
        <div style={OVERLAY(0.70)} />
        <div style={{ ...CONTENT, maxWidth: 850 }}>
          <div style={{ background: "rgba(220,38,38,0.10)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", border: "1px solid rgba(220,38,38,0.30)", borderTop: "2px solid rgba(220,38,38,0.9)", borderRadius: 16, padding: "2.5rem" }}>
            <Clock size={32} style={{ color: "#D4A84B", marginBottom: 20 }} />
            <span style={{ ...LABEL, color: C.red, marginBottom: 12 }}>The Cost of Delay</span>
            <h2 style={{ ...H2, fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)" }}>
              The 24-Hour Clock
            </h2>
            <div style={GOLDLINE} />
            <p style={{ ...P, marginBottom: 14 }}>
              Per IICRC S500, <strong style={{ color: C.white }}>mold can begin developing within 24–48 hours</strong>{" "}
              of a water intrusion event.
            </p>
            <p style={{ ...P, marginBottom: 14 }}>
              <strong style={{ color: C.white }}>Category 1 water</strong> (clean source) becomes Category 2
              after contact with building materials.
            </p>
            <p style={{ ...P, marginBottom: 14 }}>
              <strong style={{ color: C.white }}>Category 2 water</strong> becomes Category 3 (grossly contaminated)
              within 72 hours if untreated.
            </p>
            <p style={{ ...P, color: C.gold, fontWeight: 600, fontStyle: "italic", marginTop: 20 }}>
              Every hour of delayed response increases both the scope of damage and the cost of restoration.
            </p>
            <div style={{ background: "rgba(0,0,0,0.30)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "1rem 1.25rem", marginTop: 24, display: "flex", gap: 12, alignItems: "flex-start" }}>
              <BookOpen size={16} style={{ color: C.w55, flexShrink: 0, marginTop: 3 }} />
              <p style={{ ...P, fontSize: "0.8rem", color: C.w55 }}>
                This information references general IICRC S500 principles for educational purposes.
                For official standards, purchase the current IICRC S500 at{" "}
                <a href="https://iicrc.org" target="_blank" rel="noopener noreferrer" style={{ color: C.gold, fontWeight: 600 }}>iicrc.org</a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ STORM EVENTS CONTEXT ═══ */}
      <section style={{ ...SEC, backgroundColor: C.navyMid }}>
        <div style={{ ...CONTENT, maxWidth: 900 }}>
          <div style={{ marginBottom: "2rem" }}>
            <span style={LABEL}>Regional Context</span>
            <h2 style={H2}>Arizona Is Not Immune to Large-Loss Events</h2>
            <div style={GOLDLINE} />
          </div>
          <div style={{ ...PANEL, padding: "2.5rem" }}>
            <p style={{ ...P, marginBottom: 16 }}>
              In <strong style={{ color: C.white }}>March 2026, Phoenix set 7 consecutive temperature records.</strong>{" "}
              Early extreme heat strains HVAC systems and building envelopes before monsoon moisture arrives.
            </p>
            <p style={{ ...P, marginBottom: 16 }}>
              National events confirm the trend — <strong style={{ color: C.white }}>Hawaii experienced
              2 trillion gallons of flooding in 14 days in March 2026</strong>.{" "}
              <strong style={{ color: C.white }}>Washington State saw 30 rivers flood simultaneously in
              December 2025</strong>.
            </p>
            <p style={{ ...P, color: C.gold, fontWeight: 600 }}>
              Arizona&rsquo;s aging commercial infrastructure faces the same risks on a seasonal basis.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 24 }}>
              <Link href="/resources/how-to-choose" style={{ color: C.gold, fontSize: "0.85rem", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}>
                How to Choose a Contractor <ArrowRight size={14} />
              </Link>
              <Link href="/who-we-help/commercial" style={{ color: C.gold, fontSize: "0.85rem", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}>
                Commercial Services <ArrowRight size={14} />
              </Link>
              <Link href="/who-we-help/multifamily" style={{ color: C.gold, fontSize: "0.85rem", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}>
                Multifamily Services <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ DRR MONSOON READINESS ═══ */}
      <section style={{ ...SEC, padding: "60px 24px" }}>
        <div style={{ maxWidth: 850, margin: "0 auto" }}>
          <div style={{ ...PANEL, padding: "2.5rem" }}>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
              <Shield size={32} style={{ color: C.gold, flexShrink: 0, marginTop: 4 }} />
              <div style={{ flex: 1, minWidth: 260 }}>
                <span style={{ ...LABEL, marginBottom: 10 }}>Monsoon Readiness</span>
                <h3 style={{ ...H2, fontSize: "clamp(1.3rem, 2.5vw, 1.8rem)", marginBottom: 14 }}>
                  DRR Is On Call 24/7/365
                </h3>
                <p style={{ ...P, marginBottom: 16 }}>
                  Including every night of monsoon season. When the storm hits, we triage on the phone,
                  mobilize immediately, and document everything from minute one.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {["AZ ROC #349012", "IICRC Certified", `${BRAND.rating}★ ${BRAND.reviewCount} Reviews`].map((t) => (
                    <span key={t} style={{ background: "rgba(212,168,75,0.12)", border: "1px solid rgba(212,168,75,0.28)", borderRadius: 20, padding: "4px 14px", fontFamily: F.mono, fontSize: "10px", letterSpacing: "1.5px", textTransform: "uppercase", color: C.gold, fontWeight: 600 }}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section style={{ ...SEC, minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Image src="/images/arizona-landmarks/monument-valley-night-arizona.jpg" alt="Arizona night" fill style={{ objectFit: "cover" }} />
        <div style={OVERLAY(0.55)} />
        <div style={{ ...CONTENT, maxWidth: 750, textAlign: "center" }}>
          <div style={{ ...PANEL, padding: "56px 40px" }}>
            <span style={{ ...LABEL, marginBottom: 20 }}>AVAILABLE 24 · 7 · 365</span>
            <h2 style={{ color: C.white, fontFamily: F.display, fontWeight: 800, fontSize: "clamp(1.8rem, 4vw, 3rem)", lineHeight: 1.08, marginBottom: 16 }}>
              Don&rsquo;t wait for the storm<br />to find out if you&rsquo;re ready.
            </h2>
            <p style={{ ...P, fontSize: "1rem", marginBottom: 36, maxWidth: 520, margin: "0 auto 36px" }}>
              From day one to the day the project is completed. Pre-monsoon inspections available now.
            </p>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
              </div>
            <p style={{ marginTop: 28, fontFamily: F.mono, fontSize: "0.62rem", letterSpacing: "2px", textTransform: "uppercase", color: C.w30 }}>
              AZ ROC #349012 · IICRC · 24/7 Dispatch · BBB A+
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
